import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import classNames from 'classnames'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { MdAdd } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { ICategoryPosition, ILokaliseKey } from '~/types/catalog'

import LokaliseKeyInput from './LokaliseKeyInput'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { FORM_ERRORS, SESSION_STORAGE } from '~/const'
import { keys as catalogKeys, useCreateCategoryMutation, useUpdateCategoriesPositionsMutation } from '~/queries/catalog'
import { stopPropagation } from '~/utils/event'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface FormValues {
  categoryName: string
  lokaliseKey: string
}

const defaultValues: FormValues = {
  categoryName: '',
  lokaliseKey: '',
}

const validationSchema = Yup.object().shape({
  categoryName: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  lokaliseKey: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  className?: string
  lokaliseKeys: ILokaliseKey[]
  onResetNewElement: VoidFunction
}

const NewCategoryForm = ({ className, lokaliseKeys, onResetNewElement }: Props): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [nbTranslations, setNbTranslations] = useState(0)

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
  } = methods

  /** Queries & Mutations */

  const { mutateAsync: createCategory } = useCreateCategoryMutation()

  const { mutateAsync: updateCategoriesPositions } = useUpdateCategoriesPositionsMutation()

  /** Handlers */

  const displayErrorMessage = (errorKind?: string) => {
    if (errorKind === 'not_found_in_lokalise') {
      return 'Key not found in Lokalise'
    } else if (errorKind === 'already_exists') {
      return 'Category name already exists'
    } else {
      return 'Something went wrong'
    }
  }

  const handleFormSubmit = async ({ categoryName, lokaliseKey }: FormValues) => {
    const layout: ICategoryPosition[] = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE.CATALOG_CATEGORIES_LAYOUT) || ''
    )

    if (layout) {
      const index = layout.findIndex((el) => el.category_name === 'new')
      layout[index].category_name = categoryName

      try {
        await createCategory({ category_name: categoryName, enabled: false, lokalise_key: lokaliseKey })
        await updateCategoriesPositions({ positions: layout })
        await queryClient.invalidateQueries({ queryKey: catalogKeys.categories() })
        sessionStorage.clear()
        displayToast('Category successfully created', 'success')
        onResetNewElement()
        navigate(`/catalog/procolor/categories/${categoryName}`)
      } catch (e) {
        if (isAxiosError(e)) {
          displayToast(displayErrorMessage(e.response?.data?.kind), 'error')
        } else {
          captureException(e)
          displayToast(displayErrorMessage(), 'error')
        }
      }
    }
  }

  const handleNbTranslationsChange = (nbTranslations: number) => setNbTranslations(nbTranslations)

  /** Render */

  return (
    <FormProvider {...methods}>
      <form className={classNames('new-category-form-component', className)} onSubmit={handleSubmit(handleFormSubmit)}>
        <TextInput
          {...register('categoryName')}
          error={errors.categoryName?.message}
          placeholder="Category ID"
          size="small"
        />
        {nbTranslations ? (
          <span className="new-category-form-component__nb-translations">{nbTranslations} translations</span>
        ) : null}
        <LokaliseKeyInput
          disabled={lokaliseKeys.length === 0}
          lokaliseKeys={lokaliseKeys}
          name="lokaliseKey"
          onNbTranslationsChange={handleNbTranslationsChange}
        />
        <Button
          className="new-category-form-component__add-button"
          disabled={!isDirty || isSubmitting}
          onClick={stopPropagation}
          type="submit"
        >
          <MdAdd color="#fff" />
        </Button>
      </form>
    </FormProvider>
  )
}

export default NewCategoryForm
